import { AxiosResponse } from "axios";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import instance from "../../store/axios";
import { LOADING } from "../../store/actions";

export interface QueryItem {
  id: number;
  userId: number | null;
  name: string;
  email: string;
  phoneNumber: string | null;
  subject: string;
  isCallbackRequest: boolean;
  status: string;
  lastRepliedAt: string;
  createdAt: string;
  updatedAt: string;
  user: any;
  lastMessage: {
    id: number;
    ticketId: number;
    adminId: number | null;
    senderType: string;
    senderName: string;
    senderEmail: string;
    message: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface QueriesListResponse {
  tickets: QueryItem[];
  pagination: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
  };
}

export interface QueryDetailsResponse {
  id: number;
  userId: number | null;
  name: string;
  email: string;
  phoneNumber: string | null;
  subject: string;
  isCallbackRequest: boolean;
  status: string;
  lastRepliedAt: string;
  createdAt: string;
  updatedAt: string;
  user: any;
  messages?: {
    id: number;
    ticketId: number;
    adminId: number | null;
    senderType: string;
    senderName: string;
    senderEmail: string;
    message: string;
    createdAt: string;
    updatedAt: string;
  }[];
}

export interface ReplyRequest {
  message: string;
}

class QueriesService {
  private dispatch = useDispatch();

  async getQueriesList(
    page: number = 1,
    pageSize: number = 10,
    searchWord?: string,
    sortField?: string,
    sortOrder?: string,
    status?: string,
    userId?: string | number
  ): Promise<QueriesListResponse | undefined> {
    try {
      this.dispatch({ type: LOADING, payload: true });

      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("pageSize", pageSize.toString());

      if (searchWord && searchWord.trim()) {
        queryParams.append("search", searchWord.trim());
      }
      if (sortField) {
        queryParams.append("sortField", sortField);
      }
      if (sortOrder) {
        queryParams.append("sortOrder", sortOrder);
      }
      if (status) {
        queryParams.append("status", status);
      }
      if (userId) {
        queryParams.append("userId", userId.toString());
      }

      const query = queryParams.toString() ? `?${queryParams.toString()}` : "";


      const response: AxiosResponse<QueriesListResponse> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/request-info${query}`
      );

      return response.data;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", "/queries");
      return undefined;
    } finally {
      this.dispatch({ type: LOADING, payload: false });
    }
  }

  async getQueryDetails(id: number): Promise<QueryDetailsResponse | undefined> {
    try {
      this.dispatch({ type: LOADING, payload: true });

      const response: AxiosResponse<QueryDetailsResponse> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/request-info/${id}`
      );

      return response.data;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      return undefined;
    } finally {
      this.dispatch({ type: LOADING, payload: false });
    }
  }

  async replyToQuery(
    id: number,
    replyData: ReplyRequest
  ): Promise<boolean> {
    try {
      this.dispatch({ type: LOADING, payload: true });

      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/request-info/${id}/reply`,
        replyData
      );

      toast.success("Reply sent successfully");
      return true;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      return false;
    } finally {
      this.dispatch({ type: LOADING, payload: false });
    }
  }
}

export default QueriesService;